// =====================================================================
// ChangePassword.jsx — forced password change (Day 9d)
// =====================================================================
// Triggered when user.mustChangePassword === true (after admin reset).
// Validates: ≥6 chars + 1 uppercase + 1 special character.
// =====================================================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { users as usersApi } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { erreurMotDePasse as validateNew } from '../lib/motDePasse.js';
import PasswordStrengthHint from '../components/PasswordStrengthHint.jsx';

export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const isFirstLogin = !!user?.mustChangePassword;  // Day 9d — first-login mode: no currentPassword required
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null); setSuccess(null);

    if (newPassword !== confirmPassword) {
      setError('Les deux mots de passe ne correspondent pas.'); return;
    }
    const strengthErr = validateNew(newPassword);
    if (strengthErr) { setError(strengthErr); return; }

    setLoading(true);
    try {
      // First-login: backend ignores currentPassword if mustChangePassword=true
      await usersApi.changeMyPassword(isFirstLogin ? '' : currentPassword, newPassword);
      setSuccess('Mot de passe enregistré avec succès ! Redirection...');
      if (refreshUser) await refreshUser();
      setTimeout(() => navigate('/'), 1200);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur');
    } finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #eef2ff, #f5f3ff)', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 12, padding: 32, width: '100%', maxWidth: 460, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
          🔐 {isFirstLogin ? 'Choisissez votre mot de passe' : 'Nouveau mot de passe'}
        </h1>
        <p style={{ color: '#64748b', fontSize: 13, marginTop: 6, marginBottom: 20 }}>
          Bonjour <strong>{user?.name || user?.username}</strong>,
          {isFirstLogin
            ? ' bienvenue dans Brain E-Log. Choisissez un mot de passe pour sécuriser votre compte.'
            : ' votre mot de passe a été réinitialisé. Choisissez-en un nouveau pour continuer.'}
        </p>

        <form onSubmit={handleSubmit}>
          {!isFirstLogin && (
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Mot de passe actuel</label>
              <input
                type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                required autoFocus style={inputStyle}
                placeholder="Votre mot de passe actuel"
              />
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Nouveau mot de passe</label>
            <input
              type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              required autoFocus={isFirstLogin} style={inputStyle} placeholder="≥6 caractères, 1 majuscule, 1 caractère spécial"
            />
            <PasswordStrengthHint password={newPassword} />
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Confirmer le nouveau mot de passe</label>
            <input
              type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              required style={inputStyle} placeholder="Re-saisir le même mot de passe"
            />
          </div>

          {error && <div style={{ padding: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13, marginBottom: 12, border: '1px solid #fca5a5' }}>{error}</div>}
          {success && <div style={{ padding: 10, background: '#dcfce7', color: '#166534', borderRadius: 6, fontSize: 13, marginBottom: 12, border: '1px solid #86efac' }}>{success}</div>}

          <button type="submit" disabled={loading} style={{
            width: '100%', padding: '12px 16px', borderRadius: 8, border: 'none',
            background: loading ? '#94a3b8' : 'linear-gradient(135deg, #4f46e5, #6366f1)', color: 'white',
            fontSize: 14, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
          }}>
            {loading
              ? (isFirstLogin ? 'Enregistrement...' : 'Changement...')
              : (isFirstLogin ? 'Enregistrer mon mot de passe' : 'Changer mon mot de passe')}
          </button>
        </form>
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 12, color: '#475569', fontWeight: 600, marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' };
